import { AxiosPromise }                   from 'axios';
import { Layout }                         from '@/components/chart/chart.types';
import { ChartPosition }                  from '@/components/chart/helpers/chart-variables';
import { CheckboxWithOptionsValue,
  Filter,
  FILTER_TYPE,
  FilterStylesObject,
  Group,
  RequestFilter,
  Tab,
  TabContent }                                         from '@/components/filter-panel/filter-panel.types';
import { LEAD_TIME }                      from '@/helpers/variables';
import { createLeadTimeFilterConfiguration,
  getLeadTimeChartDataService,
  getLeadTimeChartSettings,
  getLeadTimeFilterAllConfigurations,
  getLeadTimeFilterConfiguration,
  removeLeadTimeFilterConfiguration,
  saveLeadTimeChartSettings,
  saveLeadTimeFiltersStylesConfiguration }                                         from '@/views/lead-time/lead-time.service';
import { createDteopFilterConfiguration,
  getDteopChartDataService,
  getDteopChartSettings,
  getDteopFilterAllConfigurations,
  getDteopFilterConfiguration,
  removeDteopFilterConfiguration,
  saveDteopChartSettings,
  saveDteopFiltersStylesConfiguration }                                         from '@/views/dteop/dteop.service';
import { Computed, ObjectWithStringKeys } from '@/types';
import { FilterConfiguration }            from '@/components/chart/chart-wrapper/store/chart-wrapper.state.store';
import { handleClientBasicError }         from '@/helpers/helpers';

export function getProperGridAreas(chartsLayoutList: Computed<Layout[]>): { [ k: string ]: string } {
  function getLayoutName(layoutCode: ChartPosition): string | undefined {
    const layoutObject: Layout | undefined = chartsLayoutList.value.find((layoutObject: Layout) => layoutObject.layoutCode === layoutCode);
    return layoutObject ? `chart_${layoutObject.chartUuid}` : undefined;
  }

  const firstRowExists = getLayoutName(ChartPosition.TOP_LEFT) || getLayoutName(ChartPosition.TOP_RIGHT);
  const secondRowExists = getLayoutName(ChartPosition.BOTTOM_LEFT) || getLayoutName(ChartPosition.BOTTOM_RIGHT);
  const firstRowGridAreas = `"${getLayoutName(ChartPosition.TOP_LEFT) || getLayoutName(ChartPosition.TOP_RIGHT)} ${getLayoutName(ChartPosition.TOP_RIGHT)
  || getLayoutName(ChartPosition.TOP_LEFT)}"`;
  const secondRowGridAreas = `"${getLayoutName(ChartPosition.BOTTOM_LEFT) || getLayoutName(ChartPosition.BOTTOM_RIGHT)} ${getLayoutName(ChartPosition.BOTTOM_RIGHT)
  || getLayoutName(ChartPosition.BOTTOM_LEFT)}"`;

  return {
    'grid-template-areas': `
          ${firstRowExists ? firstRowGridAreas : ''}
          ${secondRowExists ? secondRowGridAreas : ''}
          `,
    'grid-auto-rows': 'calc(100vh - var(--application-header-height) - (2 * var(--view-padding-vertical)))',
    'grid-template-columns': '50% 50%',
  };
}

export const getProperTabNamesDependingOnGivenProperty = (groups: Group[], property: string): string[] => groups.reduce((accGroup: string[], currGroup: Group) => {
  const appliedTabsFromCurrGroup = currGroup.tabs.reduce((accTab: string[], currTab: Tab) => {
    if (currTab[ property ]) {
      return [ ...accTab, currTab.tabName ];
    }
    return [ ...accTab ];
  }, []);
  return [ ...accGroup, ...appliedTabsFromCurrGroup ];
}, []);

const getFunctionBasedOnView = (view: string, leadTimeFunction: any, dteopFunction: any, ...args: any): AxiosPromise => {
  if (view === LEAD_TIME) {
    return leadTimeFunction(...args);
  }
  return dteopFunction(...args);
};

export const getProperChartDataService = (view: string, ...args: [ RequestFilter[], string[], string, string ]): AxiosPromise => (
  getFunctionBasedOnView(view, getLeadTimeChartDataService, getDteopChartDataService, ...args)
);

export const getProperChartSettingsService = (view: string): AxiosPromise => (
  getFunctionBasedOnView(view, getLeadTimeChartSettings, getDteopChartSettings)
);

export const saveProperChartSettingsService = (view: string, ...args: [ ObjectWithStringKeys<boolean> ]): AxiosPromise => (
  getFunctionBasedOnView(view, saveLeadTimeChartSettings, saveDteopChartSettings, ...args)
);

export const getProperFilterAllConfigurationService = (view: string): AxiosPromise => (
  getFunctionBasedOnView(view, getLeadTimeFilterAllConfigurations, getDteopFilterAllConfigurations)
);

export const getProperFilterConfigurationService = (view: string, ...args: [ string ]): AxiosPromise => (
  getFunctionBasedOnView(view, getLeadTimeFilterConfiguration, getDteopFilterConfiguration, ...args)
);

export const getProperRemoveFilterConfiguration = (view: string, ...args: [ string ]): AxiosPromise => (
  getFunctionBasedOnView(view, removeLeadTimeFilterConfiguration, removeDteopFilterConfiguration, ...args)
);

export const getProperCreateConfiguration = (view: string, ...args: [ FilterConfiguration ]): AxiosPromise => (
  getFunctionBasedOnView(view, createLeadTimeFilterConfiguration, createDteopFilterConfiguration, ...args)
);

export const getProperSaveFiltersStylesConfiguration = async (view: string, ...args: [ FilterStylesObject ]): Promise<void> => {
  try {
    await getFunctionBasedOnView(view, saveLeadTimeFiltersStylesConfiguration, saveDteopFiltersStylesConfiguration, ...args);
  } catch (e) {
    handleClientBasicError(e);
  }
};

export const extractFiltersStylesFromTabContent = (tabContent: TabContent): FilterStylesObject => tabContent.reduce((acc: FilterStylesObject, curr: Filter) => {
  if (curr.contentType === FILTER_TYPE.MULTI_DATE_RANGE || curr.contentType === FILTER_TYPE.DATE_RANGE) {
    return acc;
  }

  if (Object.keys(curr)
    .includes('values')) {
    return {
      functions: {
        ...acc.functions,
      },
      filters: {
        ...acc.filters,
        [ curr.name ]: {
          label: curr.label,
          options: curr.values.map((option: CheckboxWithOptionsValue) => ({
            color: option.color,
            label: option.label,
            name: option.name,
            order: option.order,
            isDefault: option.isDefault,
          })),
        },
      },
    };
  }

  return {
    filters: {
      ...acc.filters,
    },
    functions: {
      ...acc.functions,
      [ curr.name ]: {
        color: curr.color,
        label: curr.label,
        isDefault: curr.isDefault,
        order: curr.order,
      },
    },
  };
}, { filters: {}, functions: {} });
